/**
 * Nexuma — Install prompt utility
 *
 * - Captures the browser's `beforeinstallprompt` event so we can show our own
 *   styled install button (instead of relying on Chrome's mini-infobar).
 * - Exposes a tiny event bus so React components can subscribe to changes
 *   without prop drilling or context.
 * - Knows whether the app is already installed (standalone display-mode).
 * - Detects iOS Safari (which never fires `beforeinstallprompt`) so the UI
 *   can show a manual "tap Share → Add to Home Screen" hint instead.
 *
 * Safe to import anywhere — side-effect listeners are attached only once.
 */

const SESSION_DISMISSED_KEY = 'nexuma.installBannerDismissed';
const PERMANENT_DISMISSED_KEY = 'nexuma.installBannerDismissedForever';

let deferredPrompt = null;
let installed = false;
const listeners = new Set();

function emit() {
  const snapshot = getState();
  listeners.forEach((fn) => {
    try { fn(snapshot); } catch (_) { /* swallow */ }
  });
}

export function isStandalone() {
  if (typeof window === 'undefined') return false;
  const mq = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  // iOS Safari uses navigator.standalone instead of display-mode media query
  // eslint-disable-next-line no-restricted-globals
  return Boolean(mq || window.navigator.standalone);
}

export function isIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  const isiPad = /iPad/.test(ua) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return /iPhone|iPod/.test(ua) || isiPad;
}

export function isAndroid() {
  if (typeof navigator === 'undefined') return false;
  return /Android/i.test(navigator.userAgent || '');
}

export function isSafari() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS/.test(ua);
}

export function hasDismissed() {
  try {
    if (sessionStorage.getItem(SESSION_DISMISSED_KEY) === '1') return true;
    if (localStorage.getItem(PERMANENT_DISMISSED_KEY) === '1') return true;
  } catch (_) { /* private mode */ }
  return false;
}

export function dismissForSession() {
  try { sessionStorage.setItem(SESSION_DISMISSED_KEY, '1'); } catch (_) {}
  emit();
}

export function dismissForever() {
  try { localStorage.setItem(PERMANENT_DISMISSED_KEY, '1'); } catch (_) {}
  emit();
}

export function getState() {
  return {
    canPrompt: Boolean(deferredPrompt),
    installed: installed || isStandalone(),
    isIOS: isIOS(),
    isSafari: isSafari(),
    isAndroid: isAndroid(),
    dismissed: hasDismissed(),
  };
}

export function subscribe(fn) {
  listeners.add(fn);
  // Push the current state immediately so subscribers don't need a render race
  try { fn(getState()); } catch (_) {}
  return () => listeners.delete(fn);
}

export async function promptInstall() {
  if (!deferredPrompt) return { outcome: 'unavailable' };
  try {
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    deferredPrompt = null;
    emit();
    return { outcome: choice && choice.outcome };
  } catch (err) {
    deferredPrompt = null;
    emit();
    return { outcome: 'error', error: err };
  }
}

let initialised = false;
export function initInstallPrompt() {
  if (initialised || typeof window === 'undefined') return;
  initialised = true;

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    emit();
  });

  window.addEventListener('appinstalled', () => {
    installed = true;
    deferredPrompt = null;
    try { localStorage.setItem(PERMANENT_DISMISSED_KEY, '1'); } catch (_) {}
    emit();
  });

  // Re-emit when display-mode changes (user installs/opens as standalone mid-session)
  try {
    const mq = window.matchMedia('(display-mode: standalone)');
    const handler = () => { installed = mq.matches; emit(); };
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else if (mq.addListener) mq.addListener(handler);
  } catch (_) { /* ignore */ }
}
